/**
 * React Query 전역 Provider
 * 서버 상태 관리 및 캐싱 기능을 전역에서 사용하기 위해 설정함
 *
 * [이슈 2024.12] API 실패 시 재시도 설정 수정
 *
 * 기존 문제:
 * - retry: false로 설정되어 있어 네트워크 일시적 오류나 timeout 발생 시
 *   한 번의 실패로 데이터가 표시되지 않음
 * - 사용자가 새로고침해야만 데이터를 다시 불러올 수 있었음
 *
 * 해결:
 * - retry: 1로 설정하여 일시적 네트워크 오류 시 1회 재시도
 * - retryDelay: 1000ms로 설정하여 즉시 재시도하지 않고 잠시 대기
 * - 401 인증 에러는 apiClient 인터셉터에서 처리하므로 재시도 대상에서 제외
 */
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

/**
 * 재시도 제외 대상 판별 함수
 * 인증 관련 에러(401)는 apiClient 인터셉터에서 처리하므로 재시도하지 않음
 * @param {number} failureCount - 실패 횟수
 * @param {Error} error - 에러 객체
 * @returns {boolean} 재시도 여부
 */
function shouldRetry(failureCount, error) {
  // 최대 1회까지만 재시도
  if (failureCount >= 1) {
    return false;
  }

  // 401 에러는 apiClient 인터셉터에서 토큰 갱신 처리하므로 재시도하지 않음
  if (error?.response?.status === 401) {
    return false;
  }

  // 요청 취소된 경우 재시도하지 않음 (Alert 열린 상태 등)
  if (error?.message?.includes('취소')) {
    return false;
  }

  return true;
}

// React Query의 클라이언트 인스턴스 생성
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Alert가 열려있을 때는 refetch하지 않도록 설정
      refetchOnWindowFocus: false, // 창 포커스 시 자동 refetch 비활성화
      refetchOnReconnect: false, // 네트워크 재연결 시 자동 refetch 비활성화
      /**
       * [수정] retry: false → retry 함수로 변경
       * 이유: 네트워크 일시적 오류 시 한 번 재시도하여 안정성 향상
       * 단, 401 에러는 apiClient에서 처리하므로 재시도하지 않음
       */
      retry: shouldRetry,
      /**
       * [추가] 재시도 딜레이 설정
       * 즉시 재시도하면 같은 오류가 반복될 수 있으므로 1초 대기
       */
      retryDelay: 1000,
    },
    mutations: {
      // mutation은 사용자 액션이므로 재시도하지 않음
      retry: false,
    },
  },
});

// 애플리케이션 전체를 감싸 React Query 기능을 활성화하는 Provider 컴포넌트
export default function ReactQueryProvider({ children }) {
  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}
